import { DateTime } from "luxon";
import React from "react";
import capitalize from "../../../../../../lib/capitalize";
import formatDate from "@ui/i18n/formatDate";
import { notNull } from "diagnostics-lib/typescript";
import { useQOI } from "../../../useQOI";
import { useProjectAggegation, useProjectAggregationTargets } from "../useProjectAggregation";
import { aggregateSerieValues, aggregationByProjectAndPI } from "../../../utils/series";
import { usePeriodComparison, usePeriodComparisonDuration } from "../../../usePeriodComparison";
import type { PerformanceReporting, ReportingPeriod } from "../../../../../../api/useReporting";

const colors = ["#3069CD", "#FDA547"];

const internalLossesComparisonSerie: Highcharts.SeriesColumnOptions = {
  type: "column",
  yAxis: "performance-internal-losses-comparison",
  data: [],
  replicateInNavigator: false,
  borderWidth: 0,
};

function getSerieName(range: { min: DateTime; max: DateTime }) {
  const from = capitalize(formatDate(range.min.toJSDate(), "month-date", null));
  const to = capitalize(formatDate(range.max.toJSDate(), "month-date", null));
  return `${from} — ${to}`;
}

export function useSeries(
  reporting: PerformanceReporting,
  reportingPeriod: ReportingPeriod | null,
) {
  const [selectedPeriodComparison] = usePeriodComparison(reportingPeriod);

  const periodComparisonDuration = usePeriodComparisonDuration(reportingPeriod);
  const aggregationTargets = useProjectAggregationTargets(reporting);
  const [selectedProjectAggregation] = useProjectAggegation({});
  const [qoi] = useQOI(reporting);

  const previousPeriodComparison = React.useMemo(() => {
    if (!selectedPeriodComparison?.min || !reportingPeriod || !periodComparisonDuration)
      return null;

    const fromDate = DateTime.fromISO(reportingPeriod.min);
    const toDate = DateTime.fromISO(reportingPeriod.max);

    const previousEndDate = DateTime.fromISO(selectedPeriodComparison.min)
      .minus({ month: 1 })
      .endOf("month")
      .startOf("day");
    const previousStartDate = previousEndDate
      .minus({
        month: periodComparisonDuration.as("months"),
      })
      .plus({ day: 1 });

    return {
      min: DateTime.max(fromDate, previousStartDate),
      max: DateTime.min(toDate, previousEndDate),
    };
  }, [periodComparisonDuration, reportingPeriod, selectedPeriodComparison?.min]);

  return React.useMemo(() => {
    if (!selectedPeriodComparison?.min || !selectedPeriodComparison?.max || !reporting) return [];

    const dateRanges = [
      previousPeriodComparison,
      {
        min: DateTime.fromISO(selectedPeriodComparison.min),
        max: DateTime.fromISO(selectedPeriodComparison.max).endOf("month"),
      },
    ].filter(notNull);

    return dateRanges
      .map((comparisonPeriod, i) => {
        const from = comparisonPeriod.min.toMillis();
        const to = comparisonPeriod.max.endOf("month").toMillis();

        const data = aggregationTargets
          .map(({ id, name }) => {
            const filterByProjectAndPI = aggregationByProjectAndPI(
              reporting,
              qoi?.name ?? null,
              selectedProjectAggregation,
              id,
            );

            const periods = reporting.periods.filter(({ period }) => from <= period && period < to);

            const values = periods
              .flatMap(({ values }) => values)
              .filter(
                value =>
                  (value.value !== null || value.expected !== null) && filterByProjectAndPI(value),
              );

            const measured =
              aggregateSerieValues(
                values,
                (current, measured) => (current ?? 0) + measured,
                filterByProjectAndPI,
              ) ?? 0;

            const expected =
              aggregateSerieValues(
                values,
                (current, _, expected) => (current ?? 0) + expected,
                filterByProjectAndPI,
              ) ?? 0;

            return {
              name,
              y: ((measured - expected) / (expected || 1)) * 100,
              custom: {
                measured: measured / periods.length,
                expected: expected / periods.length,
                gap: (measured - expected) / periods.length,
              },
            };
          })
          .filter(({ y }) => y !== 0);

        return {
          ...internalLossesComparisonSerie,
          id: `internalLossesComparisonSerie-${comparisonPeriod?.min.toISO()}-${comparisonPeriod?.max.toISO()}`,
          name: getSerieName(comparisonPeriod),
          color: colors[i],
          data,
          showInLegend: data.some(({ y }) => y !== 0),
          custom: {
            current: i === 1,
          },
          index: i,
          legendIndex: i,
        };
      })
      .filter(({ data }) => data.length > 0);
  }, [
    aggregationTargets,
    previousPeriodComparison,
    reporting,
    selectedPeriodComparison?.max,
    selectedPeriodComparison?.min,
    qoi?.name,
    selectedProjectAggregation,
  ]);
}
